import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { HoverInsight } from '../types';
import MetricCard from '../components/MetricCard';
import api from '../lib/api';
import { formatIST } from '../lib/formatDate';
import { DateRangePicker } from '../components/DateRangePicker';
import { useGlobalDateRange, clampRangeToBounds } from '../context/GlobalDateRangeContext';
import { Layers, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

interface ShopifyMerchandisingProps {
  onHover: (insight: HoverInsight | null) => void;
}

interface ProductRow {
  id: number;
  title: string;
  product_type: string;
  variant_count: number;
  total_inventory: number;
}

interface Summary {
  connected: boolean;
  synced_at: string | null;
  total_products: number;
  total_inventory: number;
  low_stock_count: number;
  out_of_stock_count: number;
  avg_price: number;
  inventory_by_type: { name: string; units: number }[];
}

interface TopSellerRow {
  rank: number;
  title: string;
  sales_value: number;
  orders: number;
  product_type: string | null;
  current_stock: number | null;
  stock_alert: 'out_of_stock' | 'critical' | 'low' | null;
}

const inr = (n: number) => `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const toISO = (d: Date) => format(d, 'yyyy-MM-dd');

const StockAlertBadge = ({ alert }: { alert: TopSellerRow['stock_alert'] }) => {
  if (!alert) return <span className="text-[10px] text-slate-400">—</span>;
  const styles: Record<string, string> = {
    out_of_stock: 'bg-rose-50 text-rose-700 border-rose-100',
    critical: 'bg-orange-50 text-orange-700 border-orange-100',
    low: 'bg-amber-50 text-amber-700 border-amber-100',
  };
  const labels: Record<string, string> = { out_of_stock: 'Out of stock', critical: 'Critical', low: 'Low stock' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${styles[alert]}`} data-testid={`stock-alert-${alert}`}>
      <AlertTriangle className="h-2.5 w-2.5" />{labels[alert]}
    </span>
  );
};

export default function ShopifyMerchandising({ onHover }: ShopifyMerchandisingProps) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [lowStockTable, setLowStockTable] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [topSellers, setTopSellers] = useState<TopSellerRow[]>([]);
  const [topSellersLoading, setTopSellersLoading] = useState(true);

  const today = useMemo(() => new Date(), []);
  const ninetyDaysAgo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 90); return d; }, []);
  const { globalRange } = useGlobalDateRange();
  const [range, setRange] = useState<{ from: Date; to: Date }>(() => clampRangeToBounds(globalRange, ninetyDaysAgo, today));

  useEffect(() => {
    // Inventory is a live snapshot (Shopify doesn't give us a "catalog as of a past date" view),
    // so this module isn't date-range scoped like the transactional modules — it's always "now".
    Promise.allSettled([
      api.get('/merchandising/shopify-products/summary'),
      api.get('/merchandising/shopify-products/records', { params: { low_stock: true, page: 1, limit: 12 } }),
    ]).then(([s, r]) => {
      if (s.status === 'fulfilled') setSummary(s.value.data);
      if (r.status === 'fulfilled') setLowStockTable(r.value.data.items || []);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setTopSellersLoading(true);
    api.get('/merchandising/shopify-products/top-sellers', { params: { start_date: toISO(range.from), end_date: toISO(range.to) } })
      .then((res) => setTopSellers(res.data.items || []))
      .finally(() => setTopSellersLoading(false));
  }, [range]);

  return (
    <div className="space-y-8 animate-fade-in font-sans -m-6 md:-m-8 p-6 md:p-8 min-h-full" style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }} data-testid="shopify-merch-module-root">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e2d3ba] pb-6">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#9b7d54] block mb-1">Catalog & Inventory Health</span>
          <h1 className="text-3xl font-semibold font-arial text-slate-800 tracking-tight">Shopify Products & Inventory</h1>
          <p className="text-xs text-slate-500 mt-1 font-sans">Live product catalog and stock levels synced directly from nooe-india.myshopify.com.</p>
        </div>
        <div data-testid="shopify-merch-connection-badge" className={`px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-xs font-semibold border ${summary?.connected ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-slate-100 text-slate-400 border-slate-200'}`}>
          <Layers className="h-4.5 w-4.5" />
          <span>{summary?.connected ? `Synced ${summary.synced_at ? formatIST(summary.synced_at) : ''}` : 'Not Connected'}</span>
        </div>
      </div>

      {loading ? (
        <div data-testid="shopify-merch-loading" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-400 font-mono">Loading live Shopify catalog…</div>
      ) : !summary?.connected || summary.total_products === 0 ? (
        <div data-testid="shopify-merch-disconnected" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-500">
          Shopify isn't connected yet. Ask your admin to connect it in Admin Console → Integrations.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard platform="Shopify" title="Total Products" value={summary.total_products.toLocaleString('en-IN')} change="Live catalog" trend="neutral"
              insight={{ title: 'Catalog Size', metric: 'Total Products', value: String(summary.total_products), aiInsight: `${summary.total_products} products live in the Shopify catalog.`, prediction: 'Trend requires more history to project.', nextSteps: ['Review inventory distribution by product type below.'] }}
              onHover={onHover} />
            <MetricCard platform="Shopify" title="Total Inventory Units" value={summary.total_inventory.toLocaleString('en-IN')} change="Across all SKUs" trend="neutral"
              insight={{ title: 'Inventory Units', metric: 'Total Units', value: String(summary.total_inventory), aiInsight: `${summary.total_inventory.toLocaleString('en-IN')} units in stock across all products and variants.`, prediction: 'Trend requires more history to project.', nextSteps: ['Check low-stock products below.'] }}
              onHover={onHover} />
            <MetricCard platform="Shopify" title="Low Stock (≤10)" value={summary.low_stock_count.toLocaleString('en-IN')} change={`${summary.out_of_stock_count} out of stock`} trend={summary.low_stock_count > 0 ? 'down' : 'neutral'}
              insight={{ title: 'Low Stock Alert', metric: 'Low Stock Products', value: String(summary.low_stock_count), aiInsight: `${summary.low_stock_count} products at 10 units or below, ${summary.out_of_stock_count} fully out of stock.`, prediction: 'Low-stock SKUs may need replenishment soon.', nextSteps: ['Review the low-stock table below.'] }}
              onHover={onHover} />
            <MetricCard platform="Shopify" title="Avg Product Price" value={inr(summary.avg_price)} change="Blended" trend="neutral"
              insight={{ title: 'Average Price', metric: 'Avg Price', value: inr(summary.avg_price), aiInsight: `Blended average price of ${inr(summary.avg_price)} across the live catalog.`, prediction: 'Trend requires more history to project.', nextSteps: ['Compare pricing across product types.'] }}
              onHover={onHover} />
          </div>

          <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4">
            <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Inventory by Product Type (Top 10)</h3>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary.inventory_by_type} layout="vertical" margin={{ top: 10, right: 20, left: 20, bottom: 0 }}>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" stroke="#94a3b8" fontSize={10} tickLine={false} />
                  <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} tickLine={false} width={110} />
                  <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px' }} />
                  <Bar dataKey="units" fill="#95BF47" radius={[0, 4, 4, 0]} name="Units" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="shopify-merch-top-sellers">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Top Sellers — Ranked by Sales Volume</h3>
                <p className="text-[11px] text-slate-400 mt-0.5">Live Shopify Analytics (ShopifyQL) — real sales revenue per product, joined with the current stock snapshot.</p>
              </div>
              <DateRangePicker value={range} onChange={setRange} minDate={ninetyDaysAgo} maxDate={today} testIdPrefix="shopify-merch" />
            </div>
            {topSellersLoading ? (
              <p className="text-xs text-slate-400 font-mono py-4">Querying Shopify Analytics live…</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] font-mono uppercase text-slate-400 border-b border-slate-100">
                      <th className="py-2 pr-4">#</th>
                      <th className="py-2 pr-4">Product</th>
                      <th className="py-2 pr-4">Type</th>
                      <th className="py-2 pr-4 text-right">Sales</th>
                      <th className="py-2 pr-4 text-right">Orders</th>
                      <th className="py-2 pr-4 text-right">Stock Left</th>
                      <th className="py-2 text-right">Alert</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSellers.map((p) => (
                      <tr key={p.rank} className="border-b border-slate-50 last:border-0">
                        <td className="py-2.5 pr-4 text-slate-400 font-mono">{p.rank}</td>
                        <td className="py-2.5 pr-4 text-slate-700 font-medium max-w-xs truncate">{p.title}</td>
                        <td className="py-2.5 pr-4 text-slate-500">{p.product_type || '—'}</td>
                        <td className="py-2.5 pr-4 text-right font-semibold text-slate-800">{inr(p.sales_value)}</td>
                        <td className="py-2.5 pr-4 text-right text-slate-600">{p.orders}</td>
                        <td className="py-2.5 pr-4 text-right text-slate-600">{p.current_stock ?? '—'}</td>
                        <td className="py-2.5 text-right"><StockAlertBadge alert={p.stock_alert} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="shopify-merch-lowstock-table">
            <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Low Stock Products</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-mono uppercase text-slate-400 border-b border-slate-100">
                    <th className="py-2 pr-4">Product</th>
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 pr-4 text-right">Variants</th>
                    <th className="py-2 text-right">Units Left</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockTable.map((p) => (
                    <tr key={p.id} className="border-b border-slate-50 last:border-0">
                      <td className="py-2.5 pr-4 text-slate-700 max-w-sm truncate">{p.title}</td>
                      <td className="py-2.5 pr-4 text-slate-500">{p.product_type || 'Uncategorized'}</td>
                      <td className="py-2.5 pr-4 text-right text-slate-600">{p.variant_count}</td>
                      <td className="py-2.5 text-right font-semibold text-rose-600">{p.total_inventory}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
